import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Phone, Mail, RefreshCw, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const statusColor = { new: "bg-primary text-white", contacted: "bg-blue-500 text-white", quoted: "bg-gold text-black", won: "bg-green-600 text-white", lost: "bg-muted text-muted-foreground" };

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) navigate("/admin/login"); }, [user, loading, navigate]);

  const load = async () => {
    setBusy(true);
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data); setStats(s.data);
    } catch { toast.error("Failed to load data"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/leads/${id}`, { status }); setLeads((p) => p.map((x) => x.id === id ? { ...x, status } : x)); toast.success("Updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/leads/${id}`); setLeads((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const cards = [
    { label: "Total Leads", value: stats.total }, { label: "New", value: stats.new },
    { label: "Contacted", value: stats.contacted }, { label: "Won", value: stats.won },
  ];

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white font-display font-bold">S</span>
            <span className="font-display font-bold">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => (
            <div key={c.label} className="bg-card rounded-2xl p-6 border border-border" data-testid={`stat-${c.label}`}>
              <p className="text-3xl font-display font-bold text-primary">{c.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="p-5 border-b border-border"><h2 className="font-display text-xl">All Enquiries</h2></div>
          {leads.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground flex flex-col items-center gap-3"><Inbox className="w-10 h-10" /> No leads yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Project</TableHead>
                    <TableHead>Budget</TableHead><TableHead>Message</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-medium whitespace-nowrap">{l.name}<div className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</div></TableCell>
                      <TableCell className="whitespace-nowrap">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-sm hover:text-primary"><Phone className="w-3 h-3" /> {l.phone}</a>
                        {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mt-0.5"><Mail className="w-3 h-3" /> {l.email}</a>}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{l.project_type || "—"}<div className="text-xs text-muted-foreground">{l.source}</div></TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{l.budget || "—"}</TableCell>
                      <TableCell className="text-sm max-w-[220px]"><p className="line-clamp-2">{l.message || "—"}</p></TableCell>
                      <TableCell>
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger className="w-32 h-8" data-testid={`status-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}><Badge className={`${statusColor[s]} capitalize`}>{s}</Badge></SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`delete-${l.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
